import { normalizeHighlightColor } from './highlightColor';
/**
 * GLSN mesoscale discussion graphic, drawn entirely from bundled vector boundaries
 * (public/basemap/glsn-basemap.json). No tile provider or API key is involved, so the
 * PNG always shows states, counties and the Great Lakes.
 *
 * Pure drawing code: works with a browser canvas or node-canvas (used by the tests).
 */

export type LngLat = [number, number];

export interface Basemap {
  attribution: string;
  states: Array<{ abbr: string; name: string; glsn: boolean; label: LngLat | null; rings: LngLat[][] }>;
  counties: LngLat[][];
  lakes: Array<{ name: string; rings: LngLat[][] }>;
  provinces: Array<{ name: string; rings: LngLat[][] }>;
}

export interface GraphicInput {
  title: string;
  mdNumber?: number | null;
  concerning?: string | null;
  areasAffected?: string | null;
  tornadoProbability?: string | null;
  windProbability?: string | null;
  hailProbability?: string | null;
  issuedAt?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  issuedBy?: string | null;
  summary?: string | null;
  areaGeojson?: string | null;
  highlightColor?: string | null;
}

export const GRAPHIC_WIDTH = 1024;
export const GRAPHIC_HEIGHT = 768;
const PANEL_WIDTH = 276;
const HEADER_HEIGHT = 0;
const FOOTER_HEIGHT = 22;

const COLORS = {
  panel: '#1b2027',
  panelLine: '#2c333c',
  panelText: '#f2f5f7',
  panelMuted: '#9fb0bc',
  water: '#cfe2f0',
  land: '#ffffff',
  landOther: '#f7f7f7',
  canada: '#f2f2f2',
  county: '#c4c4c4',
  state: '#222222',
  stateOther: '#555555',
  lakeLine: '#7fa6c6',
  label: '#3a3f46',
  labelOther: '#7a7e84',
  lakeLabel: '#4f7897',
  footer: '#11151a',
};

const GREAT_LAKE_LABELS: Array<{ name: string; at: LngLat }> = [
  { name: 'Lake Superior', at: [-87.6, 47.6] },
  { name: 'Lake Michigan', at: [-87.1, 43.6] },
  { name: 'Lake Huron', at: [-82.4, 44.8] },
  { name: 'Lake Erie', at: [-81.2, 42.2] },
  { name: 'Lake Ontario', at: [-77.9, 43.6] },
];

// ---------------------------------------------------------------- time formatting

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/** "0421Z 10.11.26" (HHMMZ MM.DD.YY), the GLSN/SPC-style stamp. */
export function formatZulu(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}Z ${pad(d.getUTCMonth() + 1)}.${pad(d.getUTCDate())}.${String(d.getUTCFullYear()).slice(-2)}`;
}

/** "12:21 AM EDT Sun Oct 11" in Eastern time. */
export function formatEastern(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true,
    weekday: 'short', month: 'short', day: 'numeric', timeZoneName: 'short',
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')} ${get('weekday')} ${get('month')} ${get('day')}`;
}

// ---------------------------------------------------------------- area parsing and cleanup

/** All outer rings from a Polygon / MultiPolygon / Feature / FeatureCollection. */
export function extractRings(raw?: string | null): LngLat[][] {
  if (!raw || !raw.trim()) return [];
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  const rings: LngLat[][] = [];
  const visit = (node: any) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'FeatureCollection') (node.features || []).forEach(visit);
    else if (node.type === 'Feature') visit(node.geometry);
    else if (node.type === 'GeometryCollection') (node.geometries || []).forEach(visit);
    else if (node.type === 'Polygon' && Array.isArray(node.coordinates?.[0])) rings.push(node.coordinates[0]);
    else if (node.type === 'MultiPolygon') (node.coordinates || []).forEach((poly: any) => Array.isArray(poly?.[0]) && rings.push(poly[0]));
  };
  visit(parsed);
  return rings
    .map((ring) => ring
      .filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]))
      .map((p) => [Number(p[0]), Number(p[1])] as LngLat))
    .filter((ring) => ring.length >= 3);
}

type XY = [number, number];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);

function perimeter(points: XY[]) {
  let total = 0;
  for (let i = 0; i < points.length; i += 1) total += dist(points[i], points[(i + 1) % points.length]);
  return total;
}

/**
 * Freehand shapes usually end by overshooting or hooking back past the start point,
 * which leaves a spike where the ring closes. Trim the overlapping ends at the point
 * where the tail comes closest to the head.
 */
export function trimClosure(points: XY[]): XY[] {
  if (points.length < 8) return points;
  const total = perimeter(points);
  const n = points.length;
  const headEnd = Math.max(2, Math.floor(n * 0.15));
  const tailStart = Math.min(n - 2, Math.floor(n * 0.8));
  let best = { i: 0, j: n - 1, d: dist(points[0], points[n - 1]) };
  for (let i = 0; i <= headEnd; i += 1) {
    for (let j = tailStart; j < n; j += 1) {
      const d = dist(points[i], points[j]);
      if (d < best.d) best = { i, j, d };
    }
  }
  // Only trim when the ends really overlap (the gap is tiny compared with the shape).
  if (best.d > total * 0.03) return points;
  const trimmed = points.slice(best.i, best.j + 1);
  return trimmed.length >= 3 ? trimmed : points;
}

/** Remove vertices where the outline doubles straight back on itself (out-and-back spikes). */
export function removeSpikes(points: XY[], minAngleDeg = 20): XY[] {
  let current = points.slice();
  let changed = true;
  while (changed && current.length > 3) {
    changed = false;
    const next: XY[] = [];
    for (let i = 0; i < current.length; i += 1) {
      const prev = current[(i - 1 + current.length) % current.length];
      const point = current[i];
      const after = current[(i + 1) % current.length];
      const a = [prev[0] - point[0], prev[1] - point[1]];
      const b = [after[0] - point[0], after[1] - point[1]];
      const la = Math.hypot(a[0], a[1]);
      const lb = Math.hypot(b[0], b[1]);
      if (la < 0.5 || lb < 0.5) {
        changed = true; // duplicate point
        continue;
      }
      const angle = Math.acos(Math.max(-1, Math.min(1, (a[0] * b[0] + a[1] * b[1]) / (la * lb)))) * 180 / Math.PI;
      if (angle < minAngleDeg) {
        changed = true;
        continue;
      }
      next.push(point);
    }
    if (next.length < 3) break;
    current = next;
  }
  return current;
}

/** Douglas–Peucker simplification in pixel space (removes the staircase jitter of freehand drawing). */
export function simplify(points: XY[], tolerance: number): XY[] {
  if (points.length <= 3) return points;
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop() as [number, number];
    let maxD = 0;
    let index = -1;
    const [x1, y1] = points[start];
    const [x2, y2] = points[end];
    const len = Math.hypot(x2 - x1, y2 - y1) || 1;
    for (let i = start + 1; i < end; i += 1) {
      const [x, y] = points[i];
      const d = Math.abs((y2 - y1) * x - (x2 - x1) * y + x2 * y1 - y2 * x1) / len;
      if (d > maxD) {
        maxD = d;
        index = i;
      }
    }
    if (maxD > tolerance && index > 0) {
      keep[index] = true;
      stack.push([start, index], [index, end]);
    }
  }
  const out = points.filter((_, i) => keep[i]);
  return out.length >= 3 ? out : points;
}

/** Closed Chaikin smoothing: rounds corners without overshoot or artifacts at the seam. */
export function chaikin(points: XY[], iterations = 2): XY[] {
  let current = points;
  for (let k = 0; k < iterations; k += 1) {
    const next: XY[] = [];
    for (let i = 0; i < current.length; i += 1) {
      const p = current[i];
      const q = current[(i + 1) % current.length];
      next.push([p[0] * 0.75 + q[0] * 0.25, p[1] * 0.75 + q[1] * 0.25]);
      next.push([p[0] * 0.25 + q[0] * 0.75, p[1] * 0.25 + q[1] * 0.75]);
    }
    current = next;
  }
  return current;
}

/** Full cleanup pipeline used for drawing. Input and output are pixel coordinates. */
export function cleanRing(points: XY[]): XY[] {
  let ring = points.slice();
  // Drop an explicit closing vertex.
  if (ring.length > 3 && dist(ring[0], ring[ring.length - 1]) < 0.5) ring.pop();
  ring = trimClosure(ring);
  ring = simplify(ring, 3);
  ring = removeSpikes(ring);
  return chaikin(ring, 2);
}

// ---------------------------------------------------------------- projection and framing

export interface View {
  centerLon: number;
  centerLat: number;
  pxPerDegLat: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Default frame: the five GLSN states.
const GLSN_EXTENT = { west: -92.9, east: -80.5, south: 36.9, north: 48.3 };

export function fitView(rings: LngLat[][], box: { x: number; y: number; width: number; height: number }): View {
  let west = GLSN_EXTENT.west;
  let east = GLSN_EXTENT.east;
  let south = GLSN_EXTENT.south;
  let north = GLSN_EXTENT.north;
  const pts = rings.flat();
  if (pts.length) {
    west = Math.min(...pts.map((p) => p[0]));
    east = Math.max(...pts.map((p) => p[0]));
    south = Math.min(...pts.map((p) => p[1]));
    north = Math.max(...pts.map((p) => p[1]));
    // Padding around the area, and never zoom in so far that state context disappears.
    const padLon = Math.max((east - west) * 0.35, 0.6);
    const padLat = Math.max((north - south) * 0.35, 0.45);
    west -= padLon; east += padLon; south -= padLat; north += padLat;
    const minSpanLat = 5.5;
    if (north - south < minSpanLat) {
      const mid = (north + south) / 2;
      south = mid - minSpanLat / 2; north = mid + minSpanLat / 2;
    }
  }
  const centerLat = (north + south) / 2;
  const centerLon = (east + west) / 2;
  const cos = Math.cos(centerLat * Math.PI / 180);
  const pxPerDegLat = Math.min(box.height / (north - south), box.width / ((east - west) * cos));
  return { centerLon, centerLat, pxPerDegLat, ...box };
}

export function project(view: View, [lon, lat]: LngLat): XY {
  const cos = Math.cos(view.centerLat * Math.PI / 180);
  return [
    view.x + view.width / 2 + (lon - view.centerLon) * cos * view.pxPerDegLat,
    view.y + view.height / 2 - (lat - view.centerLat) * view.pxPerDegLat,
  ];
}

// ---------------------------------------------------------------- drawing

type Ctx = CanvasRenderingContext2D;

function pathRings(ctx: Ctx, view: View, rings: LngLat[][]) {
  ctx.beginPath();
  for (const ring of rings) {
    ring.forEach((p, i) => {
      const [x, y] = project(view, p);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
  }
}

function wrap(ctx: Ctx, text: string, maxWidth: number) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawLabel(ctx: Ctx, text: string, x: number, y: number, font: string, fill: string, halo = 'rgba(244,241,234,0.9)') {
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 4;
  ctx.strokeStyle = halo;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
}

/** Anything drawImage accepts that reports a size (HTMLImageElement, node-canvas Image). */
export type LogoImage = { width: number; height: number };
const SIDEBAR = { bg: '#2b2b2b', text: '#ffffff', muted: '#d8d8d8', tab: '#3c3c3c', tabEdge: '#8a8a8a' };
const SEVERITY = ['#fff3a0', '#ffc04d', '#ff8a3d', '#ff4040', '#ff3df5'];
const FONT = 'Arial, Helvetica, sans-serif';

function pointInRing([px, py]: LngLat, ring: LngLat[]) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** State names the area polygon covers, found by sampling a grid inside the polygon. */
export function statesInArea(rings: LngLat[][], basemap: Basemap): string[] {
  const pts = rings.flat();
  if (!pts.length) return [];
  const west = Math.min(...pts.map((p) => p[0]));
  const east = Math.max(...pts.map((p) => p[0]));
  const south = Math.min(...pts.map((p) => p[1]));
  const north = Math.max(...pts.map((p) => p[1]));
  const step = Math.max((east - west) / 40, (north - south) / 40, 0.02);
  const hits = new Set<string>();
  for (let lon = west; lon <= east; lon += step) {
    for (let lat = south; lat <= north; lat += step) {
      const p: LngLat = [lon, lat];
      if (!rings.some((r) => pointInRing(p, r))) continue;
      for (const s of basemap.states) {
        if (!hits.has(s.name) && s.rings.some((r) => pointInRing(p, r))) hits.add(s.name);
      }
    }
  }
  return basemap.states.filter((s) => hits.has(s.name)).map((s) => s.name);
}

/** Time zone for the storm area: Central west of ~87.5W, otherwise Eastern. */
function areaTimeZone(rings: LngLat[][]) {
  const pts = rings.flat();
  if (!pts.length) return 'America/New_York';
  const lon = pts.reduce((t, p) => t + p[0], 0) / pts.length;
  return lon < -87.5 ? 'America/Chicago' : 'America/New_York';
}

/** "11/03/26 12:00 AM CST" in the given zone. */
export function formatLocalShort(iso: string | null | undefined, timeZone: string) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year: '2-digit', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('month')}/${get('day')}/${get('year')} ${get('hour')}:${get('minute')} ${get('dayPeriod')} ${get('timeZoneName')}`;
}

/** 0..4 severity index for a peak-intensity value. */
export function severityLevel(kind: 'Tornado' | 'Wind' | 'Hail', value: string) {
  const nums = (value.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (!nums.length) return 0;
  const v = Math.max(...nums);
  const steps = kind === 'Tornado' ? [65, 85, 110, 135] : kind === 'Wind' ? [58, 65, 75, 90] : [1, 1.5, 2, 2.75];
  let level = 0;
  steps.forEach((s) => { if (v > s) level += 1; });
  return level;
}

function drawMap(ctx: Ctx, view: View, basemap: Basemap, areaRings: LngLat[][], color: string) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(view.x, view.y, view.width, view.height);
  ctx.clip();

  ctx.fillStyle = COLORS.water;
  ctx.fillRect(view.x, view.y, view.width, view.height);
  ctx.fillStyle = COLORS.canada;
  for (const p of basemap.provinces) {
    pathRings(ctx, view, p.rings);
    ctx.fill('evenodd');
  }
  ctx.fillStyle = COLORS.land;
  for (const s of basemap.states) {
    pathRings(ctx, view, s.rings);
    ctx.fill('evenodd');
  }
  ctx.strokeStyle = COLORS.county;
  ctx.lineWidth = 0.6;
  pathRings(ctx, view, basemap.counties);
  ctx.stroke();
  ctx.fillStyle = COLORS.water;
  ctx.strokeStyle = COLORS.lakeLine;
  ctx.lineWidth = 0.8;
  for (const lake of basemap.lakes) {
    pathRings(ctx, view, lake.rings);
    ctx.fill('evenodd');
    ctx.stroke();
  }
  ctx.lineJoin = 'round';
  ctx.strokeStyle = COLORS.stateOther;
  ctx.lineWidth = 1.6;
  for (const p of basemap.provinces) { pathRings(ctx, view, p.rings); ctx.stroke(); }
  for (const s of basemap.states) { pathRings(ctx, view, s.rings); ctx.stroke(); }

  const scale = Math.max(0.8, Math.min(1.4, view.pxPerDegLat / 70));
  for (const s of basemap.states) {
    if (!s.label) continue;
    const [x, y] = project(view, s.label);
    if (x < view.x + 30 || x > view.x + view.width - 30 || y < view.y + 15 || y > view.y + view.height - 15) continue;
    drawLabel(ctx, s.abbr, x, y, `700 ${Math.round(15 * scale)}px ${FONT}`, '#666666', 'rgba(255,255,255,0.9)');
  }

  // Discussion area: thick outline in the discussion's highlight color, no fill.
  for (const ring of areaRings) {
    const cleaned = cleanRing(ring.map((p) => project(view, p)));
    if (cleaned.length < 3) continue;
    ctx.beginPath();
    cleaned.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
    ctx.closePath();
    ctx.lineJoin = 'round';
    ctx.lineWidth = 6;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
  ctx.restore();
}

function firstSentence(text?: string | null) {
  const t = (text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  const m = t.match(/^.*?[.!?](?=\s|$)/);
  return (m ? m[0] : t).trim();
}

function drawCallout(ctx: Ctx, view: View, text: string, color: string) {
  if (!text) return;
  const maxW = Math.min(440, view.width - 60);
  let size = 18;
  let lines: string[] = [];
  for (; size >= 12; size -= 1) {
    ctx.font = `700 ${size}px ${FONT}`;
    lines = wrap(ctx, text, maxW - 20);
    if (lines.length <= 3) break;
  }
  lines = lines.slice(0, 3);
  const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 24;
  const lh = size + 4;
  const h = lines.length * lh + 12;
  const x = view.x + (view.width - w) / 2;
  const y = view.y + 40;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  lines.forEach((l, i) => ctx.fillText(l, x + w / 2, y + 6 + size + i * lh));
  ctx.textAlign = 'left';
}

function drawLogo(ctx: Ctx, cx: number, y: number, logo?: LogoImage | null) {
  if (logo && logo.width > 0 && logo.height > 0) {
    const maxW = PANEL_WIDTH - 36;
    const maxH = 150;
    const s = Math.min(maxW / logo.width, maxH / logo.height);
    const w = logo.width * s;
    const h = logo.height * s;
    ctx.drawImage(logo as CanvasImageSource, cx - w / 2, y, w, h);
    return y + h;
  }
  // Fallback drawn wordmark when /glsn-logo.png is missing.
  ctx.save();
  ctx.fillStyle = '#e8342a';
  ctx.beginPath();
  ctx.moveTo(cx - 118, y + 52);
  ctx.quadraticCurveTo(cx - 60, y - 6, cx + 120, y + 6);
  ctx.quadraticCurveTo(cx - 40, y + 8, cx - 118, y + 52);
  ctx.fill();
  ctx.font = `italic 900 50px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#ffffff';
  ctx.strokeText('GLSN', cx - 10, y + 56);
  ctx.fillStyle = '#e8342a';
  ctx.fillText('GLSN', cx - 10, y + 56);
  ctx.font = `italic 700 13px ${FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.fillText('Great Lakes Severe Network', cx, y + 76);
  ctx.restore();
  return y + 82;
}

function drawSidebar(ctx: Ctx, input: GraphicInput, rings: LngLat[][], logo?: LogoImage | null) {
  const W = PANEL_WIDTH;
  const H = GRAPHIC_HEIGHT - FOOTER_HEIGHT;
  const cx = W / 2;
  ctx.fillStyle = SIDEBAR.bg;
  ctx.fillRect(0, 0, W, H);
  const logoBottom = drawLogo(ctx, cx, 18, logo);

  const center = (text: string, y: number, font: string, color = SIDEBAR.text) => {
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.fillText(text, cx, y);
    ctx.textAlign = 'left';
  };
  const items = ([
    ['Tornado', input.tornadoProbability],
    ['Wind', input.windProbability],
    ['Hail', input.hailProbability],
  ] as Array<['Tornado' | 'Wind' | 'Hail', string | null | undefined]>).filter(([, v]) => v && v.trim());
  const until = formatLocalShort(input.validUntil, areaTimeZone(rings));
  const rowH = 34;
  // Content height, so the block is centered in the space below the logo.
  const contentH = 48 + 50 + (until ? 70 : 0) + 64 + (items.length ? 34 + items.length * (rowH + 8) : 0);
  let y = logoBottom + Math.max(24, (H - logoBottom - contentH) / 2);

  y += 40;
  center('Discussion', y, `700 42px ${FONT}`);
  y += 48;
  if (input.mdNumber) center(`#${String(input.mdNumber).padStart(4, '0')}`, y, `700 42px ${FONT}`);
  else center('(number on publish)', y - 12, `italic 16px ${FONT}`, SIDEBAR.muted);
  y += 40;
  if (until) {
    center('Valid Until:', y, `17px ${FONT}`, SIDEBAR.muted);
    y += 22;
    center(until, y, `17px ${FONT}`, SIDEBAR.muted);
    y += 48;
  } else {
    y += 18;
  }
  center('Concerning:', y, `700 19px ${FONT}`);
  y += 24;
  center((input.concerning || 'Severe Potential').trim(), y, `700 19px ${FONT}`);
  y += 40;

  if (items.length) {
    center('Most Probable Peak Intensity', y, `700 15px ${FONT}`);
    y += 14;
    for (const [label, raw] of items) {
      const value = String(raw).trim();
      const tx = 10;
      const tw = 92;
      ctx.fillStyle = SIDEBAR.tab;
      ctx.strokeStyle = SIDEBAR.tabEdge;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(tx, y);
      ctx.lineTo(tx + tw, y);
      ctx.lineTo(tx + tw + 12, y + rowH / 2);
      ctx.lineTo(tx + tw, y + rowH);
      ctx.lineTo(tx, y + rowH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.font = `700 17px ${FONT}`;
      ctx.fillText(label, tx + 8, y + 23);
      const vx = tx + tw + 16;
      const vw = W - vx - 10;
      ctx.fillStyle = SEVERITY[severityLevel(label, value)];
      ctx.fillRect(vx, y, vw, rowH);
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.font = `700 17px ${FONT}`;
      ctx.fillText(value, vx + vw / 2, y + 23);
      ctx.textAlign = 'left';
      y += rowH + 8;
    }
  }
}
function drawFooter(ctx: Ctx, basemap: Basemap, input: GraphicInput) {
  const y = GRAPHIC_HEIGHT - FOOTER_HEIGHT;
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, y, GRAPHIC_WIDTH, FOOTER_HEIGHT);
  ctx.font = `700 11px ${FONT}`;
  ctx.fillStyle = '#ffffff';
  const left = 'NOT A PRODUCT OF THE SPC';
  ctx.fillText(left, 8, y + 15);
  let leftEnd = 8 + ctx.measureText(left).width;
  const stamp = formatZulu(input.issuedAt);
  if (stamp) {
    const s = `Issued ${stamp}`;
    ctx.font = `11px ${FONT}`;
    ctx.fillStyle = '#cccccc';
    ctx.fillText(s, leftEnd + 24, y + 15);
    leftEnd += 24 + ctx.measureText(s).width;
  }
  // Credits right-aligned, shrunk/truncated so they never overlap the left text.
  ctx.fillStyle = '#9a9a9a';
  let credit = basemap.attribution || '';
  let size = 10;
  ctx.font = `${size}px ${FONT}`;
  const room = GRAPHIC_WIDTH - 8 - (leftEnd + 20);
  while (size > 8 && ctx.measureText(credit).width > room) { size -= 1; ctx.font = `${size}px ${FONT}`; }
  while (credit.length > 4 && ctx.measureText(credit).width > room) credit = `${credit.slice(0, -2).trimEnd()}…`;
  ctx.textAlign = 'right';
  ctx.fillText(credit, GRAPHIC_WIDTH - 8, y + 15);
  ctx.textAlign = 'left';
}

/** Draws the full graphic onto a GRAPHIC_WIDTH x GRAPHIC_HEIGHT canvas (SPC MD-style layout, GLSN-branded). */
export function renderGlsnGraphic(ctx: Ctx, input: GraphicInput, basemap: Basemap, logo?: LogoImage | null) {
  const color = normalizeHighlightColor(input.highlightColor);
  ctx.clearRect(0, 0, GRAPHIC_WIDTH, GRAPHIC_HEIGHT);
  const rings = extractRings(input.areaGeojson);
  const box = { x: PANEL_WIDTH, y: HEADER_HEIGHT, width: GRAPHIC_WIDTH - PANEL_WIDTH, height: GRAPHIC_HEIGHT - FOOTER_HEIGHT - HEADER_HEIGHT };
  const view = fitView(rings, box);
  drawMap(ctx, view, basemap, rings, color);
  drawCallout(ctx, view, firstSentence(input.summary) || input.title, color);
  drawSidebar(ctx, input, rings, logo);
  drawFooter(ctx, basemap, input);
  return view;
}